import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { debounceTime, map, Observable, switchMap, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import { selectIsScannedCodeValid } from 'app/state/scanned-codes/scanned-codes.selectors';

import { ErrorScannedCode, ScannedCodeType } from '../..//models';
import { FeedbackMessageService } from '../../services';

export interface ScanQrState {
    scanNotification: string | undefined;
    scannedCode: string | undefined;
}

const initialState: ScanQrState = { scanNotification: undefined, scannedCode: undefined };

@Injectable()
export class ScanQrStore extends ComponentStore<ScanQrState> {
    private store = inject(Store);
    feedbackMessageService = inject(FeedbackMessageService);

    constructor() {
        super(initialState);
    }

    scanNotification$ = this.select((state) => state.scanNotification);

    scannedCode$ = this.select((state) => state.scannedCode);

    private onScanNotification = this.updater((state, error: string | undefined) => ({
        ...state,
        scanNotification: error,
    }));

    private onScanNotificationDisposed = this.updater((state) => ({ ...state, scanNotification: undefined }));

    private onNewValidCodeScanned = this.updater((state, scannedCode: string) =>
        scannedCode === state.scannedCode
            ? state
            : {
                  ...state,
                  scannedCode,
              },
    );

    readonly showScanNotification = this.effect((error$: Observable<ErrorScannedCode | undefined>) => {
        return error$.pipe(
            tap((error) => {
                const message = this.feedbackMessageService.getScanNotificationMessage(error);
                this.onScanNotification(message);
            }),
            debounceTime(1000),
            tap(() => this.onScanNotificationDisposed()),
        );
    });

    processScannedCode = this.effect(
        (
            trigger$: Observable<{
                scannedCode: string;
                allowedCodeTypes: ScannedCodeType[];
            }>,
        ) =>
            trigger$.pipe(
                switchMap(({ allowedCodeTypes, scannedCode }) =>
                    this.store.pipe(
                        selectIsScannedCodeValid({ scannedCode, allowedCodeTypes }),
                        take(1),
                        map((isScannedCodeValid) => ({ isScannedCodeValid, scannedCode })),
                    ),
                ),
                tap(({ isScannedCodeValid, scannedCode }) => {
                    // Check only if there's an error message, will display warning notification
                    if (isScannedCodeValid.error?.message) {
                        this.showScanNotification(isScannedCodeValid.error);
                        return;
                    }

                    this.onNewValidCodeScanned(scannedCode);
                }),
            ),
    );
}
